import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { AdminLogo } from '../ui/AdminLogo';

/**
 * Bannière d'invitation à installer la PWA.
 *
 * Logique :
 * - On capture l'event `beforeinstallprompt` (uniquement émis sur Chromium quand la PWA
 *   est installable et pas déjà installée)
 * - On affiche la bannière à partir de la 2e visite (`localStorage['aq.visits']`)
 * - L'utilisateur peut accepter (déclenche le prompt natif) ou dismiss (banner masquée
 *   définitivement via `aq.install-dismissed`)
 * - Si l'app est lancée en mode standalone (déjà installée), aucun affichage
 *
 * Safari : `beforeinstallprompt` n'est pas émis. La bannière ne s'affiche pas, mais on
 * pourrait ajouter une variante "Ajouter à l'écran d'accueil" — laissée hors scope S1.
 */

const VISIT_KEY = 'aq.visits';
const DISMISS_KEY = 'aq.install-dismissed';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

function readVisitCount(): number {
  try {
    return Number(localStorage.getItem(VISIT_KEY) || '0');
  } catch {
    return 0;
  }
}

function incrementVisits(): void {
  try {
    const next = readVisitCount() + 1;
    localStorage.setItem(VISIT_KEY, String(next));
  } catch {
    // ignore (private mode)
  }
}

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS Safari
  const ios = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return ios || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

export function InstallPrompt(): JSX.Element | null {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;
    incrementVisits();

    const handler = (e: Event): void => {
      e.preventDefault();
      const evt = e as BeforeInstallPromptEvent;
      setPrompt(evt);
      // Affiche uniquement à partir de la 2e visite
      if (readVisitCount() >= 2) setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async (): Promise<void> => {
    if (!prompt) return;
    await prompt.prompt();
    const choice = await prompt.userChoice;
    if (choice.outcome === 'accepted') {
      setShow(false);
      setPrompt(null);
    }
  };

  const handleDismiss = (): void => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore
    }
    setShow(false);
  };

  if (!show || !prompt) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 bg-white border border-neutral-200 rounded-xl shadow-xl p-4 animate-in slide-in-from-bottom-2 fade-in duration-300">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <AdminLogo size={36} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-neutral-900">Installer AutoQuote Admin</h3>
          <p className="text-xs text-neutral-600 mt-1">
            Accède à ton cockpit en un clic depuis ton dock, et profite du badge de notifs +
            raccourcis.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={handleInstall}
              className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 bg-[#0066FF] text-white rounded-md hover:bg-[#0052CC] transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Installer
            </button>
            <button
              onClick={handleDismiss}
              className="text-xs font-medium px-3 py-1.5 text-neutral-600 hover:text-neutral-900 transition-colors"
            >
              Plus tard
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-neutral-400 hover:text-neutral-600"
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
